import { useMemo, useState } from "react";
import { Grid3x3, List } from "lucide-react";
import type { Product } from "../../products/hooks/useProducts";
import type { Category } from "../../products/hooks/useCategories";
import type { CardPricingRow } from "../../pos/hooks/usePosSession";
import {
  applyProductSectionFilter,
  filterProductsByCategoryId,
  type PosProductSection,
} from "../utils/productViewMappers";
import { ProductGridV2 } from "./ProductGridV2";

type ProductPanelProps = {
  filteredProducts: Product[];
  categories: Category[];
  cardPricingByProductId: Record<string, CardPricingRow>;
  productView: "grid" | "list";
  onProductViewChange: (v: "grid" | "list") => void;
  formatQty: (n: number) => string;
  onAddProduct: (productId: string, name: string) => void;
  productsLoading: boolean;
  productsError: string | null;
};

export function ProductPanel({
  filteredProducts,
  categories,
  cardPricingByProductId,
  productView,
  onProductViewChange,
  formatQty,
  onAddProduct,
  productsLoading,
  productsError,
}: ProductPanelProps) {
  const [activeSection, setActiveSection] = useState<PosProductSection>("all");
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const categoryPills = useMemo(() => {
    const activeCats = categories.filter((c) => {
      const st = (c.status ?? "").trim().toLowerCase();
      return st !== "inactive" && st !== "disabled" && st !== "off";
    });
    return [{ id: null as string | null, label: "All" }, ...activeCats.map((c) => ({ id: c.id, label: c.name }))];
  }, [categories]);

  const displayProducts = useMemo(() => {
    const byCat = filterProductsByCategoryId(filteredProducts, activeCategoryId);
    return applyProductSectionFilter(byCat, activeSection, cardPricingByProductId);
  }, [filteredProducts, activeCategoryId, activeSection, cardPricingByProductId]);

  const listKey = `${activeSection}-${activeCategoryId ?? "all"}`;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="shrink-0 border-b border-border bg-muted/30 px-3 pb-2 pt-2 sm:px-4">
        <div className="mb-2 flex items-center gap-2">
          <div className="scrollbar-hide flex min-w-0 flex-1 gap-1.5 overflow-x-auto pb-0.5">
            {(
              [
                { id: "all" as const, label: "All Products" },
                { id: "top" as const, label: "Top Selling" },
                { id: "recent" as const, label: "Recently Sold" },
                { id: "quick" as const, label: "Quick Add" },
              ] as const
            ).map((row) => {
              const active = activeSection === row.id;
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setActiveSection(row.id)}
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs transition-colors sm:px-3 sm:text-sm ${
                    active ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted"
                  }`}
                >
                  {row.id === "all" ? <Grid3x3 className="h-4 w-4 shrink-0" /> : null}
                  {row.label}
                </button>
              );
            })}
          </div>
          <div className="flex shrink-0 items-center rounded-lg border border-border bg-card p-0.5">
            <button
              type="button"
              onClick={() => onProductViewChange("grid")}
              className={`rounded-md p-1.5 transition-colors ${
                productView === "grid" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted"
              }`}
              aria-pressed={productView === "grid"}
              title="Grid"
            >
              <Grid3x3 className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => onProductViewChange("list")}
              className={`rounded-md p-1.5 transition-colors ${
                productView === "list" ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted"
              }`}
              aria-pressed={productView === "list"}
              title="List"
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="hide-scrollbar-sm-md flex gap-1.5 overflow-x-auto pb-0.5">
          {categoryPills.map((c) => {
            const active = activeCategoryId === c.id;
            return (
              <button
                key={c.id ?? "all"}
                type="button"
                onClick={() => setActiveCategoryId(c.id)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-all sm:text-sm ${
                  active ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <ProductGridV2
          products={displayProducts}
          cardPricingByProductId={cardPricingByProductId}
          productView={productView}
          formatQty={formatQty}
          onAddProduct={onAddProduct}
          productsLoading={productsLoading}
          productsError={productsError}
          listKey={listKey}
        />
      </div>
    </div>
  );
}
